import { AppleOutlined, CheckCircleFilled, CloudUploadOutlined, FileOutlined, WindowsOutlined } from '@ant-design/icons';
import { App, Button, DatePicker, Form, Input, Progress, Switch, Tag, Upload } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { api, putInstaller } from '../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../components';
import { formatBytes, formatDate, timeAgo, useLoad } from '../lib';

const platformInfo = {
  windows: { label: 'Windows', icon: <WindowsOutlined />, accept: '.exe,.msi', file: 'EXE or MSI', tone: 'sky' },
  macos: { label: 'macOS', icon: <AppleOutlined />, accept: '.dmg,.pkg,.zip', file: 'DMG, PKG or ZIP', tone: 'slate' }
};

export function DesktopReleasesPage() {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [files, setFiles] = useState({ windows: null, macos: null });
  const [uploading, setUploading] = useState('');
  const [progress, setProgress] = useState(0);
  const { data, error, loading, reload } = useLoad(() => api('/v1/admin/releases/desktop').then((body) => body.release));

  useEffect(() => {
    if (data) form.setFieldsValue({ version: data.version, title: data.title, releasedAt: data.releasedAt ? dayjs(data.releasedAt) : dayjs(), mandatory: Boolean(data.mandatory) });
  }, [data, form]);

  async function detailsFromForm() {
    const values = await form.validateFields();
    return { version: values.version.trim(), title: (values.title || '').trim(), releasedAt: values.releasedAt.format('YYYY-MM-DD'), mandatory: Boolean(values.mandatory) };
  }

  async function publish(platform) {
    let details;
    try { details = await detailsFromForm(); } catch { return; }
    const file = files[platform];
    if (!file) return message.error(`Choose the ${platformInfo[platform].label} installer.`);
    setUploading(platform);
    setProgress(8);
    try {
      const body = { platform, ...details, fileName: file.name, sizeBytes: file.size };
      const prepared = await api('/v1/admin/releases/desktop/upload', { method: 'POST', body: JSON.stringify(body) });
      setProgress(25);
      await putInstaller(prepared.objectKey, file, (part) => setProgress(25 + Math.round(part * 55)));
      setProgress(80);
      await api('/v1/admin/releases/desktop/complete', { method: 'POST', body: JSON.stringify({ ...body, objectKey: prepared.objectKey }) });
      setProgress(100);
      message.success(`${platformInfo[platform].label} ${details.version} is live.`);
      setFiles((current) => ({ ...current, [platform]: null }));
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'The installer could not be published.');
    } finally {
      setUploading('');
      setProgress(0);
    }
  }

  async function publishReady() {
    if (files.windows) await publish('windows');
    if (files.macos) await publish('macos');
  }

  async function saveDetails() {
    let details;
    try { details = await detailsFromForm(); } catch { return; }
    setUploading('details');
    try {
      await api('/v1/admin/releases/desktop', { method: 'PUT', body: JSON.stringify(details) });
      message.success(details.mandatory ? 'Desktops must install this version.' : 'Desktops can skip this update.');
      await reload();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'The release details could not be saved.');
    } finally {
      setUploading('');
    }
  }

  const queued = [files.windows && 'windows', files.macos && 'macos'].filter(Boolean);
  const busy = Boolean(uploading);

  return <>
    <PageHeader
      title="Desktop releases"
      description="One version for both installers. Drop the EXE and DMG, then publish. Desktops that can reach Kivonex Cloud are told about this update."
      actions={queued.length > 0 ? <Button type="primary" icon={<CloudUploadOutlined />} loading={busy} onClick={() => void publishReady()}>
        Publish {queued.length === 2 ? 'both' : platformInfo[queued[0]].label}
      </Button> : null}
    />
    <ErrorNotice error={error} />
    {loading && !data ? <FormSkeleton sections={3} /> : <Form form={form} layout="vertical" requiredMark={false} onFinish={() => void (queued.length ? publishReady() : saveDetails())}>
      <div className="stats stats-4">
        <StatCard label="Version" tone="indigo" icon={<FileOutlined />} loading={loading && !data} value={data?.version || '—'} foot={data?.title || 'No title yet'} />
        <StatCard label="Update" tone={data?.mandatory ? 'amber' : 'green'} icon={<CheckCircleFilled />} loading={loading && !data} value={data?.mandatory ? 'Required' : 'Optional'} foot={data?.releasedAt ? `Released ${formatDate(data.releasedAt)}` : 'Set a release date'} />
        <StatCard label="Windows" tone={data?.platforms?.windows ? 'green' : 'slate'} icon={<WindowsOutlined />} loading={loading && !data} value={data?.platforms?.windows ? formatBytes(data.platforms.windows.sizeBytes) : 'Not published'} foot={data?.platforms?.windows ? data.platforms.windows.fileName : 'Drop an EXE or MSI'} />
        <StatCard label="macOS" tone={data?.platforms?.macos ? 'green' : 'slate'} icon={<AppleOutlined />} loading={loading && !data} value={data?.platforms?.macos ? formatBytes(data.platforms.macos.sizeBytes) : 'Not published'} foot={data?.platforms?.macos ? data.platforms.macos.fileName : 'Drop a DMG, PKG or ZIP'} />
      </div>

      <Panel title="Release" description="Shared by Windows and Mac. You can change this later without uploading again." extra={<Button type="primary" htmlType="button" onClick={() => void saveDetails()} loading={uploading === 'details'} disabled={busy && uploading !== 'details'}>Save</Button>}>
        <div className="release-details">
          <Form.Item label="Version" name="version" rules={[{ required: true, whitespace: true, message: 'Enter a version.' }]}><Input placeholder="0.1.0" className="mono" /></Form.Item>
          <Form.Item label="Title" name="title"><Input placeholder="WhatsApp send and desktop installers" /></Form.Item>
          <Form.Item label="Date" name="releasedAt" rules={[{ required: true, message: 'Choose a date.' }]}><DatePicker style={{ width: '100%' }} placeholder="Release date" /></Form.Item>
          <Form.Item name="mandatory" valuePropName="checked" label="Required" extra="App waits for this version">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </div>
      </Panel>

      <div className="release-platform-grid">
        {Object.entries(platformInfo).map(([platform, info]) => {
          const current = data?.platforms?.[platform];
          const chosen = files[platform];
          const active = uploading === platform;
          return <section key={platform} className={`release-card${current ? ' is-live' : ''}${chosen ? ' is-ready' : ''}`}>
            <header className="release-card-head">
              <span className={`release-os tone-${info.tone}`}>{info.icon}</span>
              <div>
                <h3>{info.label}</h3>
                <p>{info.file}</p>
              </div>
              {current ? <Tag color="success" variant="filled">Live</Tag> : <Tag variant="filled">Waiting</Tag>}
            </header>
            {current && <div className="release-live">
              <CheckCircleFilled />
              <div>
                <strong title={current.fileName}>{current.fileName}</strong>
                <span>{formatBytes(current.sizeBytes)} · {timeAgo(current.uploadedAt)}</span>
              </div>
            </div>}
            <Upload.Dragger
              accept={info.accept}
              maxCount={1}
              fileList={chosen ? [chosen] : []}
              beforeUpload={(file) => { setFiles((value) => ({ ...value, [platform]: file })); return false; }}
              onRemove={() => setFiles((value) => ({ ...value, [platform]: null }))}
              disabled={busy}
            >
              <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
              <p className="ant-upload-text">{chosen ? chosen.name : `Drop the ${info.label} installer`}</p>
              <p className="ant-upload-hint">{chosen ? formatBytes(chosen.size) : 'Or click to choose a file'}</p>
            </Upload.Dragger>
            {active && <Progress percent={progress} status="active" showInfo />}
            <Button type="primary" block icon={<CloudUploadOutlined />} loading={active} disabled={busy || !chosen} onClick={() => void publish(platform)}>
              Publish {info.label}
            </Button>
          </section>;
        })}
      </div>
      <p className="release-note">Publishing one installer does not remove the other. A required update can be turned off later without uploading again.</p>
    </Form>}
  </>;
}
